import { TYPO } from "styles/Typo";
import { AnimatePresence, motion } from "framer-motion";
import usePortfolioRiskUtil from "utils/portfolio/risk";
import { useCallback, useState } from "react";
import Button from "components/common/buttons/buttons";
import { useRouter } from "next/router";
import { useHandleImageErrorVer2 } from "utils/useHandleImageError";
import WeightPeiChart from "./WeightPieChart";
import { useSession } from "next-auth/react";
import { useParams } from "next/navigation";
import WeightBarChart from "./WeightBarChart";
import { DONUT_CHART_PALETTE } from "utils/constant/STYLE";
import { SkeletonBlock3 } from "components/skeleton";
import { trackPageAction } from "lib/analytics/core/actions";

const RiskDetail = ({ item, last }: { item: any; last: boolean }) => {
  const Meta = {
    cvaret: {
      title: "상승 여력",
      value: item?.EXP_CVaRET,
      valueTestId: "pf-risk-card-exp-cvar-ret",
    },
    cvarnts: {
      title: "하락 리스크",
      value: item?.EXP_CVaRNTS,
      valueTestId: "pf-risk-card-exp-cvar-nts",
    },
    CVaR_LV: {
      title: "변동성",
      value:
        item?.CVaR_LV === "Danger" || item?.CVaR_LV === "Caution"
          ? "높음"
          : "낮음",
      valueTestId: "pf-risk-card-cvar-lv",
    },
  };
  const Section = ({}: {}) => {
    return (
      <div className={`w-full flex`}>
        <div className="grow flex-1 w-full flex flex-col items-center justify-center text-center gap-1 border-r border-hairlineBorder ">
          <div className="px-4 py-3 flex flex-col gap-1">
            <p className={`${TYPO.TYPO7_1_RG} text-gray70 text-center`}>
              {"변동성"}
            </p>
            <p
              className={`${TYPO.TYPO7_1_RG} text-gray80 text-center px-2 bg-gray15 rounded-[4px]`}
              data-testid={Meta?.CVaR_LV?.valueTestId}
            >
              {!!Meta?.CVaR_LV?.value ? `${Meta?.CVaR_LV?.value}` : "-"}
            </p>
          </div>
        </div>
        <div className="grow flex-1 w-full flex flex-col items-center justify-center text-center gap-1 border-r border-hairlineBorder ">
          <div className="px-4 py-3 flex flex-col gap-1">
            <p className={`${TYPO.TYPO7_1_RG} text-gray70 text-center`}>
              {Meta?.cvaret?.title}
            </p>
            <p
              className={`${TYPO.TYPO7_1_RG} text-red60 text-center px-2 bg-red10 rounded-[4px]`}
              data-testid={Meta?.cvaret?.valueTestId}
            >
              {!!Meta?.cvaret?.value ? `+${Meta?.cvaret?.value}%` : "-"}
            </p>
          </div>
        </div>
        <div className="grow flex-1 w-full flex flex-col items-center justify-center text-center gap-1 ">
          <div className="px-4 py-3 flex flex-col gap-1">
            <p className={`${TYPO.TYPO7_1_RG} text-gray70 text-center`}>
              {Meta?.cvarnts?.title}
            </p>
            <p
              className={`${TYPO.TYPO7_1_RG} text-blue60 text-center px-2 bg-blue10 rounded-[4px]`}
              data-testid={Meta?.cvarnts?.valueTestId}
            >
              {!!Meta?.cvarnts?.value ? `-${Meta?.cvarnts?.value}%` : "-"}
            </p>
          </div>
        </div>
      </div>
    );
  };

  const router = useRouter();
  const isMainPage = router.asPath === "/?type=portfolio";
  const gtmId = isMainPage
    ? "pf-main-risk-stock-detail-accordion"
    : "pf-teaser-risk-stock-detail-accordion";

  return (
    <motion.div
      initial={{ height: 0, opacity: 0 }}
      animate={{ height: "auto", opacity: 1 }}
      exit={{ height: 0, opacity: 0 }}
      transition={{ duration: 0.3, ease: "easeInOut" }}
      className={` ${
        last ? "" : "border-b border-gray20"
      }  flex w-full overflow-hidden`}
      data-testid={`pf-risk-card-detail-section`}
      data-gtm-id={gtmId}
    >
      <div className="flex w-full">
        <Section />
      </div>
    </motion.div>
  );
};

function MainPortfolioRisk({ portId }: { portId: number | null }) {
  const {
    stockRisk,
    portRisk,
    maxBaseDt,
    isLoading,
    isError,
    weight,
    isSameWeight,
  } = usePortfolioRiskUtil(Number(portId));
  const router = useRouter();
  const { data: session } = useSession();
  const { getStockLogoSafe, onError } = useHandleImageErrorVer2();
  const [openedIndexes, setOpenedIndexes] = useState<number[]>([0]);
  const { portId: teaserPortId } = useParams();

  const handleToggle = (index: number) => {
    trackPageAction(
      openedIndexes.includes(index) ? "collapse_pf_risk" : "expand_pf_risk"
    );
    setOpenedIndexes((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
    );
  };

  const portLevel = portRisk?.CVaR_LV;

  const level = portLevel
    ? portLevel === "Danger" || portLevel === "Caution"
      ? "high"
      : "low"
    : "";

  const desc = portLevel
    ? portLevel === "Danger" || portLevel === "Caution"
      ? "이정도면 높은 편이에요"
      : "이정도면 낮은 편이에요"
    : "";
  if ((!stockRisk || isLoading) && !isError)
    return (
      <div className="px-4 pt-[30px]">
        <SkeletonBlock3 width={"100%"} height={300} />
      </div>
    );
  return (
    <div>
      <div className="px-6 pt-[30px] flex justify-between items-center">
        <p className={`${TYPO.TYPO5_SB} text-gray90`}>비중</p>
        {isSameWeight && (
          <Button
            type="tertiary"
            size="sm"
            text="실제 비중 입력하기"
            clickHandler={() => {
              router.push(
                `/portfolio/${teaserPortId || portId}/setting?from=${
                  router.asPath
                }`
              );
            }}
            btnClass="!bg-grayOpacity10"
            dataGtmId={
              teaserPortId
                ? "pf-teaser-diag-weight-realWeight-btn"
                : "pf-main-diag-weight-realWeight-btn"
            }
          />
        )}
      </div>
      <div className="p-4 flex flex-col gap-[10px]">
        <div className="flex flex-col    bg-1 rounded-[14px] ">
          <div className="flex flex-col gap-1  px-4 py-[10px]">
            <div className="flex gap-[6px] items-center">
              <p className={`${TYPO.TYPO7_MD} text-gray90`}>
                {level === "high" ? "공격형" : "안정형"}
              </p>
              <p
                className={`${TYPO.TYPO9_MD} text-gray70 bg-gray20 px-[6px] py-[1px] rounded-[20px]`}
              >
                {`변동성 ${level === "high" ? "높음" : "낮음"}`}
                {/* {portRisk?.volatility ? `최대 ${portRisk?.volatility}%` : "-"} */}
              </p>
            </div>
            <p className={`whitespace-pre-line ${TYPO.TYPO7_1_RG} text-gray70`}>
              {desc}
            </p>
          </div>
          <div className="px-4 pt-2 pb-4">
            <WeightBarChart data={weight} />
          </div>
          {/* <div className="flex flex-col pt-6 px-4 gap-[14px] pb-[9px]">
            <WeightPeiChart data={weight} height={110} width={110} />
          </div> */}

          {/* <Button
            type="secondary"
            size="sm"
            clickHandler={goHighScorePage}
            text={level === "high" ? "변동성 낮추기" : "변동성 올리기"}
            dataTestId="pf-risk-unlock-button"
          /> */}
        </div>
        {stockRisk && (
          <div className="flex flex-col bg-1 border border-gray10 rounded-[14px] overflow-hidden">
            {stockRisk?.map((item: any, index: number) => {
              const targetWeight = weight?.find(
                (weightItem: any) =>
                  String(weightItem.ITEM_CD_DL) === String(item.ITEM_CD_DL)
              );
              return (
                <div key={index}>
                  <div
                    className={`flex items-center gap-2 px-4 py-3 cursor-pointer transition-colors ${
                      stockRisk?.length - 1 > index ||
                      openedIndexes.includes(index)
                        ? "border-b border-gray20"
                        : ""
                    }`}
                    onClick={() => handleToggle(index)}
                    data-testid={`pf-risk-card-detail-accordion`}
                  >
                    {/* <Image
                  src={getStockLogoSafe(item.ITEM_CD_DL)}
                  alt={item.ITEM_KR_NM}
                  width={24}
                  height={24}
                  className="rounded-full"
                  onError={() => onError(item.ITEM_CD_DL)}
                /> */}
                    <div
                      className="h-3 w-3 rounded-[4px]"
                      style={{
                        background:
                          DONUT_CHART_PALETTE[
                            index % DONUT_CHART_PALETTE.length
                          ],
                      }}
                    />
                    <p className={`${TYPO.TYPO7_RG} text-gray70 grow`}>
                      {item.ITEM_KR_NM}
                    </p>
                    <p
                      className={`${TYPO.TYPO7_MD} text-gray90`}
                      data-testid={`pf-risk-card-cvar-lv`}
                    >
                      {!!targetWeight?.WEIGHT
                        ? `${(Number(targetWeight?.WEIGHT) * 100)?.toFixed(0)}%`
                        : "-"}
                      {/* {item.CVaR_LV
                    ? `${
                        item?.CVaR_LV === "Danger" ||
                        item?.CVaR_LV === "Caution"
                          ? "높음"
                          : "낮음"
                      }`
                    : "-"} */}
                    </p>
                    <motion.div
                      animate={{
                        rotate: openedIndexes.includes(index) ? 180 : 0,
                      }}
                      transition={{ duration: 0.2 }}
                      className="w-3 h-3 flex items-center justify-center"
                    >
                      <svg
                        width="12"
                        height="8"
                        viewBox="0 0 12 8"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M1 1.5L6 6.5L11 1.5"
                          stroke="var(--gray40)"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </motion.div>
                  </div>
                  <AnimatePresence>
                    {openedIndexes.includes(index) && (
                      <RiskDetail
                        item={item}
                        last={stockRisk?.length - 1 === index}
                      />
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        )}
        {/* {maxBaseDt && (
          <p className={`${TYPO.TYPO8_RG} text-gray60 text-right`}>
            마지막 업데이트 ・ {maxBaseDt}
          </p>
        )} */}
      </div>
    </div>
  );
}

export default MainPortfolioRisk;
